import { useEffect, useState } from "react";
import { api } from "./api";
import {
  ExternalLink,
  ChevronRight,
  Save,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
  ShieldAlert,
} from "lucide-react";

const STATUSES = [
  { key: "not_started", label: "Not started", color: "bg-white/10 text-white/60" },
  { key: "applied", label: "Applied", color: "bg-sky-500/15 text-sky-300" },
  { key: "pending", label: "Pending review", color: "bg-yellow-500/15 text-yellow-300" },
  { key: "verified", label: "Verified / Live", color: "bg-green-500/15 text-green-400" },
  { key: "rejected", label: "Rejected", color: "bg-red-500/15 text-red-300" },
  { key: "not_applicable", label: "Not applicable", color: "bg-white/5 text-white/40" },
];

const statusMeta = (s) => STATUSES.find((x) => x.key === s) || STATUSES[0];

const AdminHubs = () => {
  const [hubs, setHubs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showBanner, setShowBanner] = useState(true);

  const refresh = async () => {
    const r = await api.listHubs();
    setHubs(r.data.hubs || []);
  };
  useEffect(() => { refresh(); }, []);

  const grouped = hubs.reduce((acc, h) => {
    (acc[h.category] = acc[h.category] || []).push(h);
    return acc;
  }, {});

  const counts = {
    verified: hubs.filter((h) => h.status === "verified").length,
    applied: hubs.filter((h) => ["applied", "pending"].includes(h.status)).length,
    not_started: hubs.filter((h) => h.status === "not_started").length,
    total: hubs.length,
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">3PL Hub Connections</h1>
        <p className="text-white/60 mt-1 text-sm">
          Track your registration status on external directories, matchmakers, and marketplaces where
          shippers find 3PLs.
        </p>
      </div>

      {showBanner && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-4 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-red-200">Password rotation reminder</div>
            <div className="text-red-200/80 mt-1">
              You shared a password in chat that stores across logs. Rotate it now on any account it&apos;s
              used on. Turbo BD Console does <b>not</b> store passwords for external platforms — only your
              contact email, status, and notes.
            </div>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-red-300 hover:text-red-100">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI value={counts.total} label="Total hubs" />
        <KPI value={counts.verified} label="Verified / Live" tone="green" />
        <KPI value={counts.applied} label="In progress" tone="orange" />
        <KPI value={counts.not_started} label="Not started" tone="red" />
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <section key={cat}>
          <div className="text-xs uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold mb-3">
            {cat}
          </div>
          <div className="space-y-2">
            {items.map((h) => {
              const isOpen = expanded === h.slug;
              const s = statusMeta(h.status);
              return (
                <div key={h.slug} className="rounded-xl bg-[#0f1512] border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpanded(isOpen ? null : h.slug)}
                    data-testid={`hub-row-${h.slug}`}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition"
                  >
                    <ChevronRight className={`h-4 w-4 text-white/40 transition shrink-0 ${isOpen ? "rotate-90" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{h.name}</span>
                        {!h.self_serve && (
                          <span className="text-[10px] uppercase tracking-wider rounded-full bg-white/5 px-2 py-0.5 text-white/50">
                            Human review
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 mt-0.5 truncate">{h.requires}</div>
                    </div>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap ${s.color}`}>
                      {h.status === "verified" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : h.status === "pending" ? <Clock className="h-3 w-3 mr-1" /> : null}
                      {s.label}
                    </span>
                  </button>
                  {isOpen && <HubDetail hub={h} onSaved={refresh} />}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

const HubDetail = ({ hub, onSaved }) => {
  const [form, setForm] = useState({
    status: hub.status,
    contact_email: hub.contact_email || "",
    contact_name: hub.contact_name || "",
    signup_date: hub.signup_date || "",
    listing_url: hub.listing_url || "",
    notes: hub.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setSavedOk(false); };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateHub(hub.slug, form);
      setSavedOk(true);
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="border-t border-white/10 p-5 bg-black/20">
      <div className="grid md:grid-cols-[1fr_auto] gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Instructions</div>
          <div className="text-sm text-white/80 leading-relaxed">{hub.notes || hub.requires}</div>
        </div>
        <a
          href={hub.signup_url} target="_blank" rel="noreferrer"
          className="self-start inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-zam-orange)] hover:bg-[#d16600] px-4 py-2 text-sm font-semibold transition whitespace-nowrap"
          data-testid={`hub-signup-${hub.slug}`}
        >
          Open signup <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Status</span>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm">
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
        <Input label="Contact name" value={form.contact_name} onChange={(v) => set("contact_name", v)} />
        <Input label="Contact email" value={form.contact_email} onChange={(v) => set("contact_email", v)} />
        <Input label="Applied on" value={form.signup_date} onChange={(v) => set("signup_date", v)} type="date" />
        <Input label="Listing / profile URL" value={form.listing_url} onChange={(v) => set("listing_url", v)} placeholder="https://…" />
        <label className="block sm:col-span-2 lg:col-span-3">
          <span className="text-[11px] uppercase tracking-wider text-white/50">Internal notes</span>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm resize-none" />
        </label>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="text-[11px] text-white/40 flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5" />
          Never store passwords here — use a real password manager (1Password, Bitwarden, LastPass).
        </div>
        <div className="flex items-center gap-3">
          {savedOk && <span className="text-xs text-green-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Saved</span>}
          <button onClick={save} disabled={saving} data-testid={`hub-save-${hub.slug}`} className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--tt-green)] hover:bg-[var(--tt-green-dark)] px-4 py-2 text-sm font-semibold transition disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const KPI = ({ value, label, tone }) => {
  const c = tone === "green" ? "text-green-400" : tone === "orange" ? "text-[var(--tt-zam-orange)]" : tone === "red" ? "text-white/40" : "text-white";
  return (
    <div className="rounded-2xl bg-[#0f1512] border border-white/10 p-4">
      <div className={`text-3xl font-display font-bold ${c}`}>{value}</div>
      <div className="text-xs text-white/60 mt-1">{label}</div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", placeholder }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-white/50">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-[var(--tt-green)] transition" />
  </label>
);

export default AdminHubs;
